import classNames from 'classnames';
import type {
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  ReactNode,
  RefObject,
} from 'react';

import { css } from '@sorare/css';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAlertCircle } from '#src/atoms/icons/system/iconAlertCircle.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

const root = css`
  --lit-edge: var(--c-nd-500);

  position: relative;
  width: 100%;
  padding: 0 var(--intermediate-unit);
  color: var(--c-white);
  border: 1px solid var(--c-common-tertiary);
  border-radius: 9999px;
  background: var(--c-nd-50);
  box-shadow: inset 0 0 9px var(--c-nd-150);
  backdrop-filter: blur(8px);

  /* Lit top edge of the glass. A gradient can't be a border-color, so it is
   * painted as a 1px ring on top of the solid border and masked hollow. */
  @supports (mask-composite: exclude) {
    &:before {
      content: '';
      position: absolute;
      /* inset:0 would resolve to the padding box and land the ring one pixel
       * inside; -1px lines it up with the border itself. */
      inset: -1px;
      border: 1px solid transparent;
      border-radius: inherit;
      background: linear-gradient(180deg, var(--lit-edge), transparent 50%)
        border-box;
      mask:
        linear-gradient(#fff, #fff) padding-box,
        linear-gradient(#fff, #fff);
      mask-composite: exclude;
      pointer-events: none;
    }
  }

  /* The .hover/.focus hooks let the state-matrix story render states that
   * neither props nor a static screenshot can reach. */
  &:hover,
  &.hover {
    background: var(--c-nd-100);
  }

  &:focus-within,
  &.focus {
    background: var(--c-black);
    box-shadow: inset 0 0 9px var(--c-nd-200);

    & p {
      opacity: 1;
    }
  }

  /* The edge takes the error hue: a white highlight over the red reads as a
   * grey smudge, and the unlit --c-rare-tertiary border reads as a grey
   * hairline against the glow. */
  &.error {
    --lit-edge: var(--c-rare);

    border-color: var(--c-rare-tertiary);
    background: rgba(var(--c-rgb-red-300), 0.16);
    box-shadow: inset 0 0 9px var(--c-rare);
  }

  &.disabled {
    color: var(--c-nd-500);
    background: var(--c-nd-50);
    box-shadow: inset 0 0 9px var(--c-nd-150);
  }
`;

const medium = css`
  height: var(--quadruple-unit);
  font: var(--t-label-m);
  /* https://thingsthemselves.com/no-input-zoom-in-safari-on-iphone-the-pixel-perfect-way/ */
  font-size: var(--fs-16);

  @media ${laptopAndAbove} {
    font-size: var(--fs-14);
  }
`;

const large = css`
  height: calc(5 * var(--unit));
  font: var(--t-label-l);
`;

const field = css`
  resize: none;
  width: 100%;
  min-width: 0;
  height: inherit;
  font: inherit;
  letter-spacing: inherit;
  color: currentColor;
  border: none;
  background-color: transparent;

  &::placeholder,
  &[type='button'] {
    color: currentColor;
    text-overflow: ellipsis;
    opacity: 0.6;
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

const sizes = {
  medium,
  large,
};

type Props = {
  className?: string;
  id?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  inputRef?: RefObject<HTMLInputElement | null>;
  type?: HTMLInputTypeAttribute;
  size?: keyof typeof sizes;
  error?: boolean;
} & Partial<Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>>;

export const TextInput = ({
  className,
  id,
  startAdornment,
  endAdornment,
  inputRef,
  type = 'text',
  size = 'large',
  error = false,
  ...rest
}: Props) => {
  return (
    <Horizontal
      className={classNames(root, sizes[size], className, {
        error,
        disabled: rest.disabled,
      })}
    >
      {startAdornment}
      {/* Ahead of the spread so a consumer passing its own value still wins. */}
      <input
        className={field}
        id={id}
        aria-invalid={error || undefined}
        {...rest}
        ref={inputRef}
        type={type}
      />
      {/* The board puts the alert in the same slot the end adornment occupies,
       * so erroring takes it over rather than crowding in beside it. */}
      {error ? (
        <FontAwesomeIcon icon={iconAlertCircle} color="var(--c-red-400)" />
      ) : (
        endAdornment
      )}
    </Horizontal>
  );
};
